import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  ShieldCheck,
  HeartHandshake,
  CreditCard,
  MapPin,
  ArrowRight,
  CheckCircle2,
  Flag,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { AU_STATES } from "../lib/constants";
import BrandMark from "./BrandMark";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "WWCC verified", color: "#10B981" },
  { icon: HeartHandshake, label: "Australian-owned", color: "#F59E0B" },
  { icon: CreditCard, label: "AUD pricing", color: "#1E3FE0" },
  { icon: CheckCircle2, label: "Pay only after class", color: "#C4B5FD" },
];

const FOOTER_LEGAL_LINKS = [
  { to: "/legal/terms", label: "Terms" },
  { to: "/legal/privacy", label: "Privacy" },
  { to: "/legal/child-safety", label: "Child safety" },
  { to: "/legal/refund", label: "Payments & cancellations" },
];

const STATE_REGIONS = {
  NSW: "Sydney",
  VIC: "Melbourne",
  QLD: "Brisbane",
  SA: "Adelaide",
  WA: "Perth",
  TAS: "Hobart",
  ACT: "Canberra",
  NT: "Darwin",
};

const REPORT_MIN_WORD_COUNT = 20;
const REPORT_CATEGORIES = [
  { value: "service", label: "Website / product issue" },
  { value: "payment", label: "Billing or payment" },
  { value: "safety", label: "Safety concern" },
  { value: "other", label: "Other" },
];

function countWords(value) {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

export default function Footer({ compact = false }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState("service");
  const [reportDescription, setReportDescription] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const legalLinks = FOOTER_LEGAL_LINKS;

  useEffect(() => {
    if (!reportOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !reportSubmitting) setReportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [reportOpen, reportSubmitting]);

  const subscribe = (e) => {
    e.preventDefault();
    const trimmed = String(email || "").trim();
    const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!looksValid) {
      toast.error("Please enter a valid email address.");
      return;
    }
    // No real newsletter endpoint yet — soft success.
    setSubscribed(true);
    toast.success("You're on the list! We'll be in touch soon.");
    setEmail("");
  };

  const openReport = () => {
    setReportCategory("service");
    setReportDescription("");
    setReportOpen(true);
  };

  const submitReport = async (event) => {
    event.preventDefault();
    if (!user) return;
    const trimmed = reportDescription.trim();
    if (countWords(trimmed) < REPORT_MIN_WORD_COUNT) {
      toast.error(`Please describe the issue in ${REPORT_MIN_WORD_COUNT} words or more.`);
      return;
    }
    setReportSubmitting(true);
    try {
      await api.post("/safety-reports", {
        category: reportCategory,
        description: trimmed,
      });
      toast.success("Report submitted. Our team will review.");
      setReportOpen(false);
      setReportDescription("");
    } catch (error) {
      toast.error(formatApiError(error) || "Could not submit your report.");
    } finally {
      setReportSubmitting(false);
    }
  };

  const reportModal = reportOpen ? (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="footer-report-modal-title"
      data-testid="footer-report-modal"
    >
      <button
        type="button"
        aria-label="Close report issue modal"
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-[4px]"
        onClick={() => {
          if (!reportSubmitting) setReportOpen(false);
        }}
      />
      <div className="relative w-full max-w-lg rounded-[1.6rem] border border-[#d5dcf3] bg-white p-5 shadow-[0_28px_70px_rgba(11,20,38,0.28)] sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="tiny-caps text-aus-cobalt">Support</div>
            <h2
              id="footer-report-modal-title"
              className="mt-1 font-heading text-xl font-bold text-aus-onyx"
            >
              Report an issue
            </h2>
            <p className="mt-1.5 text-sm text-aus-onyx/65 leading-relaxed">
              Tell us about a website problem or general platform concern. Tutor-specific
              reports stay on the tutor profile page.
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost shrink-0 text-xs"
            onClick={() => setReportOpen(false)}
            disabled={reportSubmitting}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {user ? (
          <form className="mt-4 space-y-3" onSubmit={submitReport}>
            <label className="block text-xs font-semibold text-aus-onyx/70">
              Category
              <select
                value={reportCategory}
                onChange={(e) => setReportCategory(e.target.value)}
                className="glass-input mt-1.5 w-full px-3 py-2.5 text-sm text-aus-onyx"
              >
                {REPORT_CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs font-semibold text-aus-onyx/70">
              What happened?
              <textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                rows={5}
                className="glass-input mt-1.5 w-full px-3 py-2.5 text-sm text-aus-onyx"
                placeholder="Describe the issue clearly so we can investigate."
              />
            </label>
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => setReportOpen(false)}
                disabled={reportSubmitting}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary text-sm" disabled={reportSubmitting}>
                {reportSubmitting ? "Submitting…" : "Submit report"}
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 rounded-2xl border border-aus-cobalt/15 bg-aus-cobalt/[0.04] p-4">
            <p className="text-sm text-aus-onyx/70 leading-relaxed">
              Sign in to submit a report through Austuition, or contact us if you prefer email.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to="/contact" className="btn-primary text-sm" onClick={() => setReportOpen(false)}>
                Contact us
              </Link>
              <Link to="/login" className="btn-secondary text-sm" onClick={() => setReportOpen(false)}>
                Sign in
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;

  if (compact) {
    return (
      <>
        <footer
          className="mt-8 border-t border-slate-200/70 bg-white/80 text-aus-onyx/70"
          data-testid="site-footer"
        >
          <div className="container-aus py-4 flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center justify-between text-xs">
            <div>
              <div className="font-semibold">Austuition</div>
              <div className="text-aus-onyx/55 text-xs mt-0.5">Helping students learn with confidence.</div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {legalLinks.map((link) => (
                <Link key={link.to} to={link.to} className="hover:text-aus-cobalt transition-colors">
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" className="hover:text-aus-cobalt transition-colors">Contact</Link>
              <button
                type="button"
                onClick={openReport}
                className="hover:text-aus-cobalt transition-colors inline-flex items-center gap-1"
                data-testid="footer-report-issue"
              >
                <Flag className="w-3 h-3" /> Report an issue
              </button>
            </div>
            <div className="text-aus-onyx/55">© {new Date().getFullYear()} Austuition</div>
          </div>
        </footer>
        {reportModal}
      </>
    );
  }

  return (
    <>
    <footer
      className="relative overflow-hidden text-white"
      data-testid="site-footer"
      style={{ background: "linear-gradient(180deg, #10162F 0%, #121a34 48%, #182442 100%)" }}
    >
      <div className="footer-cream-bridge" aria-hidden="true" />
      <div
        className="h-px w-full"
        aria-hidden="true"
        style={{
          background: "linear-gradient(90deg, transparent 8%, rgba(238, 230, 255, 0.85) 38%, rgba(230, 185, 76, 0.45) 52%, rgba(238, 125, 169, 0.35) 62%, transparent 92%)",
        }}
      />

      <div className="relative container-aus pt-8 lg:pt-10 pb-6">
        {/* ============== NEWSLETTER GLASS CARD ============== */}
        <div
          className="relative overflow-hidden p-4 lg:p-6 mb-6"
          style={{
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.10)",
            borderRadius: "1.25rem",
          }}
        >

          <div className="relative grid lg:grid-cols-[1fr_auto] gap-4 items-center">
            <div>
              <span
                className="inline-flex items-center gap-1.5 mb-2.5 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-[0.16em] uppercase"
                style={{
                  background: "rgba(255, 255, 255, 0.10)",
                  border: "1px solid rgba(255, 255, 255, 0.18)",
                  color: "rgba(255, 255, 255, 0.80)",
                }}
              >
                <Mail className="w-3 h-3" /> Newsletter
              </span>
              <h3 className="font-heading text-xl lg:text-2xl font-bold leading-tight text-balance text-white">
                Get the inside track.
              </h3>
              <p className="text-white/78 mt-1.5 max-w-xl text-sm leading-relaxed">
                New tutor releases, study guides, and curriculum-specific tips for VCE, HSC, SACE, IB, UCAT and medicine
                interviews. One email a fortnight — no spam, ever.
              </p>
            </div>

            <form onSubmit={subscribe} className="w-full lg:w-[360px]">
              <div
                className="flex flex-col gap-2 p-1.5 sm:flex-row sm:items-center"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "0.75rem",
                }}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Mail className="w-4 h-4 ml-3 text-white/45 shrink-0" />
                  <input
                    data-testid="newsletter-email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 min-w-0 bg-transparent border-0 outline-none py-2 text-sm text-white placeholder:text-white/35"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  className="shrink-0 inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold transition-all hover:brightness-105"
                  style={{
                    background: "linear-gradient(135deg, #FFE89B 0%, #F5B335 50%, #C19A2E 100%)",
                    color: "#2A1A00",
                    boxShadow: "0 5px 14px rgba(217, 160, 18, 0.45), inset 0 -2px 0 rgba(120, 70, 0, 0.25)",
                  }}
                >
                  {subscribed ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Subscribed
                    </>
                  ) : (
                    <>
                      Subscribe <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
              <p className="text-[10px] text-white/35 mt-1.5 ml-2">We respect your privacy. Unsubscribe any time.</p>
            </form>
          </div>
        </div>

        {/* ============== TRUST BADGES ROW ============== */}
        <div
          className="relative flex flex-wrap items-center justify-center gap-1.5 lg:gap-2 mb-6 py-2.5 px-2.5 lg:px-4"
          style={{
            background: "rgba(255,255,255,0.075)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "1.25rem",
          }}
        >
          {TRUST_BADGES.map((b) => {
            const Icon = b.icon;
            return (
              <div
                key={b.label}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full"
                style={{
                  background: "rgba(255,255,255,0.09)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: `${b.color}22`, border: `1px solid ${b.color}55` }}
                >
                  <Icon className="w-3 h-3" style={{ color: b.color }} />
                </div>
                <span className="text-[10px] font-semibold text-white/85 whitespace-nowrap">{b.label}</span>
              </div>
            );
          })}
        </div>

        {/* ============== 5-COLUMN LINK GRID ============== */}
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-5 mb-6">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-2 mb-3">
              <BrandMark sizeClass="w-9 h-9" />
              <div className="flex flex-col leading-none">
                <span className="font-heading text-[1.5rem] font-bold tracking-tight text-white">Austuition</span>
                <span className="text-[9px] font-bold tracking-[0.22em] uppercase text-aus-gold mt-0.5">
                  Australia
                </span>
              </div>
            </Link>
            <p className="text-[0.9rem] text-white/72 leading-relaxed max-w-sm">
              Affordable tutoring. Exceptional tutors. Australia-wide, online-first marketplace for senior curricula, IB,
              UCAT and medicine interviews.
            </p>
            <div className="mt-4">
              <a
                href="mailto:hello@austuition.com.au"
                aria-label="Email Austuition"
                className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-white/88 transition-all hover:-translate-y-0.5 hover:text-white"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.12)" }}
                >
                  <Mail className="w-3.5 h-3.5 text-white/80" />
                </span>
                <span>Email us</span>
              </a>
            </div>
          </div>

          <FooterColumn
            title="For Learners"
            links={[
              { to: "/find-a-tutor", label: "Find a Tutor" },
              { to: "/how-it-works", label: "How It Works" },
              { to: "/subjects", label: "Subjects" },
              { to: "/about", label: "About Us" },
              { to: "/tutor-tiers", label: "Tutor Tiers" },
            ]}
          />

          <FooterColumn
            title="For Tutors"
            links={[
              { to: "/become-a-tutor", label: "Become a Tutor" },
              { to: "/tutor-tiers", label: "Tier system" },
              { to: "/how-it-works", label: "Workflow" },
              { to: "/dashboard/edit-profile?tab=general", label: "Edit profile" },
            ]}
          />

          <FooterColumn
            title="Company"
            links={[
              { to: "/about", label: "About Us" },
              { to: "/faq", label: "FAQ" },
              { to: "/contact", label: "Contact" },
            ]}
            actions={[
              {
                label: "Report an issue",
                onClick: openReport,
                testId: "footer-report-issue",
              },
            ]}
          />

          <FooterColumn
            title="Legal"
            links={legalLinks}
          />
        </div>

        {/* ============== AUSTRALIAN STATES PILLS ============== */}
        <div
          className="flex flex-wrap items-center gap-1.5 py-3 mb-4"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.08)",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-aus-gold mr-1.5 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Tutors in
          </span>
          {AU_STATES.map((s) => (
            <Link
              key={s}
              to={`/find-a-tutor?state=${s}`}
              className="text-[10px] font-semibold px-2 py-1 rounded-full transition-all hover:-translate-y-0.5"
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.8)",
              }}
              title={`${s} — ${STATE_REGIONS[s] || ""}`}
            >
              {s} <span className="text-white/35 ml-0.5">· {STATE_REGIONS[s]}</span>
            </Link>
          ))}
        </div>

        {/* ============== BOTTOM BAR ============== */}
        <div className="flex flex-col sm:flex-row gap-2 justify-between items-center text-[10px] text-white/58">
          <div className="flex items-center gap-2 flex-wrap">
            <span>© {new Date().getFullYear()} Austuition Pty Ltd</span>
            <span className="text-white/20">·</span>
            <span>Independent tutoring marketplace</span>
            <span className="text-white/20">·</span>
            <span>Tutors are independent service providers</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="tiny-caps text-aus-gold" style={{ fontSize: "0.65rem" }}>Proudly Australian</span>
            <span className="text-base">🇦🇺</span>
          </div>
        </div>
      </div>
    </footer>
    {reportModal}
    </>
  );
}

function FooterColumn({ title, links, actions = [] }) {
  return (
    <div className="lg:col-span-2">
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-aus-gold mb-2.5">{title}</div>
      <ul className="space-y-1.5 text-[0.92rem]">
        {links.map((l) => (
          <li key={l.to + l.label}>
            <Link to={l.to} className="text-white/78 hover:text-white transition-colors inline-flex items-center gap-1">
              {l.label}
            </Link>
          </li>
        ))}
        {actions.map((action) => (
          <li key={action.label}>
            <button
              type="button"
              onClick={action.onClick}
              className="text-white/78 hover:text-white transition-colors inline-flex items-center gap-1.5"
              data-testid={action.testId}
            >
              <Flag className="w-3.5 h-3.5" />
              {action.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
